// src/hooks/parts/usePartsLines.ts
// Phase 2 — Internal Parts Line CRUD + rollup hooks.
//
// AUTHORITY (FF-B / FF-E):
//   The `RequireFeature("partsLineItems")` wrapper at every mutation
//   call site is a UI gate only. Server authority is enforced by the
//   `trg_enforce_parts_line_entitlement` trigger on `parts_lines`
//   (migration `20260501130000_ff_b_parts_entitlement_triggers.sql`),
//   which raises `insufficient_privilege` when the shop is not
//   CATALYST/ENTERPRISE tier and lacks the `parts_line_items` addon.
//   FF-E adds the matching READ-side gate via the
//   `ff_e_parts_lines_select_entitled` RESTRICTIVE policy
//   (migration `20260502120000_ff_e_financial_read_authority.sql`),
//   so a non-entitled shop cannot read parts_lines even if rows
//   exist (e.g., service-role-seeded or post-downgrade).
//   A sessionStorage feature-flag override cannot bypass either layer.
//   Existing role-based RLS (OWNER/MANAGER/SERVICE_WRITER) remains
//   intact as a separate PERMISSIVE layer.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route parts soft-delete `deleted_at` write through clock.
import { clock } from '@/utils/clock';
import {
  rollupPartsForTicket,
  type FeeRule,
  type TaxabilityCategories,
  type TaxSettingsLike,
  type PartsRollupResult,
} from '@/lib/parts-financial/calculator';

export type PartsLineStatus =
  | 'needed' | 'researching' | 'quoted' | 'awaiting_approval' | 'ordered'
  | 'backordered' | 'received' | 'installed' | 'returned' | 'canceled';

export type PartsPricingSource =
  | 'manual' | 'matrix' | 'optiguard' | 'flat_rate' | 'imported' | 'other';
export type PartsPricingMethod =
  | 'matrix_per_unit' | 'matrix_grouped' | 'flat_markup' | 'manual_entry' | 'optiguard' | 'imported';

export interface PartsLine {
  id: string;
  shop_id: string;
  ticket_id: string;
  job_id: string | null;
  vendor_name: string | null;
  vendor_part_number: string | null;
  description: string;
  category: string | null;
  quantity: number;
  unit_cost: number | null;
  /** @deprecated Mirror of actual_sell_each maintained by DB trigger. Read via `readActualSell()`; write only via authority-contract fields. */
  unit_sell: number | null;
  taxable: boolean;
  status: PartsLineStatus;
  availability_text: string | null;
  eta_text: string | null;
  core_charge: number;
  source_type: string;
  source_reference: any;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Phase 3.5 — Authority-contract fields
  actual_sell_each: number | null;
  actual_sell_total: number | null;
  suggested_sell_each: number | null;
  suggested_sell_total: number | null;
  pricing_source: PartsPricingSource;
  pricing_method: PartsPricingMethod | null;
  manual_price_locked: boolean;
  // Snapshot fields (set on create or reapply; never mutated retroactively)
  quantity_snapshot: number | null;
  cost_each_snapshot: number | null;
  cost_total_snapshot: number | null;
  matrix_quantity_mode_snapshot: 'per_unit' | 'grouped' | null;
  matrix_tier_min_snapshot: number | null;
  matrix_tier_max_snapshot: number | null;
  matrix_multiplier_snapshot: number | null;
  // Phase 7 R2 — Procurement mirror fields. Separate lifecycle from `status`.
  procurement_status: ProcurementStatus;
  purchase_order_line_id: string | null;
  last_ordered_at: string | null;
  last_received_at: string | null;
  last_returned_at: string | null;
  // Phase 9 R3 — Inventory linkage. `inventory_item_id` is form-writable;
  // `inventory_reservation_qty` and `inventory_consumed_qty` are RPC-only.
  inventory_item_id: string | null;
  inventory_reservation_qty: number;
  inventory_consumed_qty: number;
}

// Phase 7 R2 — Frozen canonical procurement enum (matches DB CHECK).
export type ProcurementStatus =
  | 'not_ordered'
  | 'ordered'
  | 'partially_received'
  | 'received'
  | 'partially_returned'
  | 'returned';

export type PartsLineInsert = Omit<PartsLine,
  'id' | 'created_at' | 'updated_at' | 'deleted_at' | 'created_by' |
  'actual_sell_each' | 'actual_sell_total' | 'suggested_sell_each' | 'suggested_sell_total' |
  'pricing_source' | 'pricing_method' | 'manual_price_locked' |
  'quantity_snapshot' | 'cost_each_snapshot' | 'cost_total_snapshot' |
  'matrix_quantity_mode_snapshot' | 'matrix_tier_min_snapshot' |
  'matrix_tier_max_snapshot' | 'matrix_multiplier_snapshot'
> & {
  created_by?: string | null;
  // Authority-contract fields are optional on insert (caller may seed manual price or suggestion).
  actual_sell_each?: number | null;
  actual_sell_total?: number | null;
  suggested_sell_each?: number | null;
  suggested_sell_total?: number | null;
  pricing_source?: PartsPricingSource;
  pricing_method?: PartsPricingMethod | null;
  manual_price_locked?: boolean;
  quantity_snapshot?: number | null;
  cost_each_snapshot?: number | null;
  cost_total_snapshot?: number | null;
  matrix_quantity_mode_snapshot?: 'per_unit' | 'grouped' | null;
  matrix_tier_min_snapshot?: number | null;
  matrix_tier_max_snapshot?: number | null;
  matrix_multiplier_snapshot?: number | null;
};
export type PartsLineUpdate = Partial<Omit<PartsLine, 'id' | 'shop_id' | 'created_at' | 'updated_at'>>;

const partsKey = (shopId: string, ticketId: string) => ['parts-lines', shopId, ticketId] as const;

// ── List ──────────────────────────────────────────────────────────────────
export function useTicketPartsLines(shopId: string | undefined, ticketId: string | undefined) {
  return useQuery({
    queryKey: partsKey(shopId ?? '', ticketId ?? ''),
    enabled: !!shopId && !!ticketId,
    queryFn: async (): Promise<PartsLine[]> => {
      const { data, error } = await (supabase as any)
        .from('parts_lines')
        .select('*')
        .eq('shop_id', shopId)
        .eq('ticket_id', ticketId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as PartsLine[];
    },
  });
}

// ── Create ────────────────────────────────────────────────────────────────
export function useCreatePartsLine() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: PartsLineInsert) => {
      const payload = { ...input, created_by: input.created_by ?? user?.id ?? null };
      const { data, error } = await (supabase as any)
        .from('parts_lines')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data as PartsLine;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: partsKey(row.shop_id, row.ticket_id) });
    },
  });
}

// ── Update ────────────────────────────────────────────────────────────────
export function useUpdatePartsLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; shopId: string; ticketId: string; patch: PartsLineUpdate }) => {
      const { data, error } = await (supabase as any)
        .from('parts_lines')
        .update(args.patch)
        .eq('id', args.id)
        .select()
        .single();
      if (error) throw error;
      return data as PartsLine;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: partsKey(vars.shopId, vars.ticketId) });
    },
  });
}

// ── Soft delete ───────────────────────────────────────────────────────────
export function useDeletePartsLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; shopId: string; ticketId: string }) => {
      const { error } = await (supabase as any)
        .from('parts_lines')
        .update({ deleted_at: clock.now().toISOString() })
        .eq('id', args.id);
      if (error) throw error;
      return { id: args.id };
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: partsKey(vars.shopId, vars.ticketId) });
    },
  });
}

// ── Status update convenience ─────────────────────────────────────────────
export function useUpdatePartsLineStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; shopId: string; ticketId: string; status: PartsLineStatus }) => {
      const { data, error } = await (supabase as any)
        .from('parts_lines')
        .update({ status: args.status })
        .eq('id', args.id)
        .select()
        .single();
      if (error) throw error;
      return data as PartsLine;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: partsKey(vars.shopId, vars.ticketId) });
    },
  });
}

// ── Rollup ────────────────────────────────────────────────────────────────
export interface UseRollupArgs {
  shopId: string | undefined;
  ticketId: string | undefined;
  taxRules: { categories: TaxabilityCategories };
  taxSettings: TaxSettingsLike;
  feeRules?: FeeRule[];
}

export function useTicketPartsRollup(args: UseRollupArgs): {
  rollup: PartsRollupResult | null;
  isLoading: boolean;
  lines: PartsLine[];
} {
  const { data, isLoading } = useTicketPartsLines(args.shopId, args.ticketId);
  const lines = data ?? [];
  if (!args.shopId || !args.ticketId) return { rollup: null, isLoading, lines };

  const rollup = rollupPartsForTicket({
    lines: lines.map((l) => ({
      id: l.id,
      job_id: l.job_id,
      quantity: Number(l.quantity),
      unit_cost: l.unit_cost == null ? null : Number(l.unit_cost),
      // DMC-H: pass Phase 3.5 authority-contract fields straight through.
      // The pre-DMC-H IIFE that coalesced these into `unit_sell` was
      // retired once `computePartLineEconomics` lost its legacy fallback
      // (live audit confirmed zero parts_lines rows depended on it).
      actual_sell_each: l.actual_sell_each == null ? null : Number(l.actual_sell_each),
      actual_sell_total: l.actual_sell_total == null ? null : Number(l.actual_sell_total),
      pricing_method: l.pricing_method,
      manual_price_locked: !!l.manual_price_locked,
      taxable: l.taxable,
      core_charge: Number(l.core_charge ?? 0),
    })),
    taxRules: args.taxRules,
    taxSettings: args.taxSettings,
    feeRules: args.feeRules ?? [],
  });

  return { rollup, isLoading, lines };
}
